import { t, useLanguage, countryName } from "@/lib/i18n";
import React, { useEffect, useState } from "react";
import { getCountries, getStatesOfCountry, getCitiesOfState } from "@/lib/locations";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

const selectClass = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-50";
const sorted = (items) => [...items].sort((a, b) => a.name.localeCompare(b.name));

// Ignore responses for an earlier selection or an unmounted form.
function useLocations(load, key, retry) {
  const [result, setResult] = useState({ items: [], loading: false, error: false, key });
  useEffect(() => {
    let active = true;
    setResult({ items: [], loading: Boolean(key), error: false, key });
    if (key) load().then((items) => {
      if (active) setResult({ items: sorted(items), loading: false, error: false, key });
    }).catch(() => {
      if (active) setResult({ items: [], loading: false, error: true, key });
    });
    return () => { active = false; };
    // The key captures every parameter used by the loader.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, retry]);
  return result.key === key ? result : { items: [], loading: Boolean(key), error: false };
}

export default function AddressFields({ value, onChange }) {
  useLanguage();
  const [retry, setRetry] = useState(0);
  const countries = useLocations(getCountries, "countries", retry);
  const country = countries.items.find((item) => item.name === value.country);
  const countryCode = country?.iso2 || (value.country === "India" ? "IN" : "");
  const states = useLocations(() => getStatesOfCountry(countryCode), countryCode, retry);
  const state = states.items.find((item) => item.name === value.state);
  const cities = useLocations(() => getCitiesOfState(countryCode, state.iso2), state ? `${countryCode}:${state.iso2}` : "", retry);
  const error = countries.error || states.error || cities.error;
  return <div className="space-y-3">
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <div className="min-w-0 space-y-1.5">
        <Label htmlFor="input-member-country">{t("Country")}</Label>
        <select id="input-member-country" data-testid="input-member-country" autoComplete="country-name" className={selectClass}
          value={value.country || ""} onChange={(event) => onChange({ country: event.target.value, state: "", city: "" })}>
          <option value="">{t("Select country")}</option>
          {value.country && !country && <option value={value.country}>{countryName(value.country, countryCode)}</option>}
          {countries.items.map((item) => <option key={item.iso2} value={item.name}>{countryName(item.name, item.iso2)}</option>)}
        </select>
        {countries.loading && <p className="text-xs text-stone-500" role="status">{t("Loading countries…")}</p>}
      </div>
      <div className="min-w-0 space-y-1.5">
        <Label htmlFor="input-member-state">{t("State/Province")}</Label>
        {states.items.length > 0 || states.loading ? <select id="input-member-state" data-testid="input-member-state" className={selectClass}
          autoComplete="address-level1" value={value.state || ""} disabled={!countryCode || states.loading}
          onChange={(event) => onChange({ state: event.target.value, city: "" })}>
          <option value="">{states.loading ? t("Loading states…") : t("Select state/province")}</option>
          {value.state && !state && <option value={value.state}>{value.state}</option>}
          {states.items.map((item) => <option key={item.id} value={item.name}>{item.name}</option>)}
        </select> : <Input id="input-member-state" data-testid="input-member-state" autoComplete="address-level1" maxLength={150}
          disabled={!value.country} value={value.state || ""} placeholder={t("Enter state/province")}
          onChange={(event) => onChange({ state: event.target.value, city: "" })} />}
      </div>
      <div className="min-w-0 space-y-1.5">
        <Label htmlFor="input-member-city">{t("City")}</Label>
        <Input id="input-member-city" data-testid="input-member-city" autoComplete="address-level2" maxLength={150}
          list="member-city-options" value={value.city || ""} disabled={!value.country || states.loading || cities.loading}
          placeholder={cities.loading ? t("Loading cities…") : t("Select or enter city")}
          onChange={(event) => onChange({ city: event.target.value })} />
        <datalist id="member-city-options">
          {[...new Set(cities.items.map((item) => item.name))].map((name) => <option key={name} value={name} />)}
        </datalist>
        <p className="text-xs text-stone-500">{t("Choose a suggested city or enter one if it is missing.")}</p>
      </div>
    </div>
    {error && <p role="alert" className="text-sm text-amber-800">{t("Some locations could not be loaded. You can enter the state and city manually. ")}<button type="button" className="underline" onClick={() => setRetry((current) => current + 1)}>{t("Retry")}</button></p>}
    <p className="text-xs text-stone-500">{t("Location data: ")}<a href="https://github.com/dr5hn/countries-states-cities-database" target="_blank" rel="noreferrer" className="underline">{t("CountryStateCity")}</a> (<a href="https://opendatacommons.org/licenses/odbl/1-0/" target="_blank" rel="noreferrer" className="underline">{t("ODbL")}</a>).</p>
  </div>;
}
